/**
 * CSV Export Utilities
 *
 * Functions for generating and downloading CSV files from data.
 * Handles proper escaping of special characters and formatting.
 */

/**
 * Escapes a value for use in a CSV file
 * @param {any} value - The value to escape
 * @returns {string} The escaped value
 */
export function escapeCSVValue(value) {
  if (value === null || value === undefined) return '';

  const stringValue = String(value);

  // If contains comma, newline, or quote, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Generates a CSV string from data and column configuration
 * @param {Array} data - Array of data objects to export
 * @param {Array} columns - Array of column keys to include (in order)
 * @param {Object} columnMapping - Mapping of column keys to { label, format } objects
 * @returns {string} CSV string with UTF-8 BOM
 */
export function generateCSV(data, columns, columnMapping) {
  const BOM = '\uFEFF'; // UTF-8 BOM for Excel compatibility

  // Build header row from column labels
  const headers = columns.map(columnKey => {
    const mapping = columnMapping[columnKey];
    return mapping?.label || columnKey;
  });
  const headerRow = headers.map(escapeCSVValue).join(',');

  // Build data rows
  const dataRows = data.map(row => {
    return columns.map(columnKey => {
      const mapping = columnMapping[columnKey];

      // Use format function if provided, otherwise use raw value
      const value = mapping?.format ? mapping.format(row) : row[columnKey];

      return escapeCSVValue(value);
    }).join(',');
  });

  // Combine header and data rows
  return BOM + headerRow + '\n' + dataRows.join('\n');
}

/**
 * Triggers a CSV file download in the browser
 * @param {string} csvContent - The CSV content string
 * @param {string} filename - The filename for the download (without extension)
 */
export function downloadCSV(csvContent, filename) {
  // Create a blob with UTF-8 encoding
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create a temporary download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.display = 'none';

  // Trigger the download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
